// Custom middleware to simplify API calls

// Example use:
// const response = await apiRequestExternal(
//     `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/example/query?example=example`, // url
//     'POST', // method
//     { key1: 'value1' }, // body
//     { Authorization: yourToken } // headers
//   );

import config from "../config";
import { auth } from "../util/auth";
import { updateBadges } from "../util/badgeUpdater";
import { localLogger, logger } from "../util/logger";
import { updateSnackbar } from "../util/snackbarUpdater";
import {
  incrementFailureCount,
  resetFailureCount,
} from "./apiRequestFailureTracker";

// Refresh auth token
const getNewToken = async () => {
  logger("trying token refresh");
  localLogger("auth", auth);
  localLogger("auth.user:", auth.user);
  localLogger("auth.currentUser:", auth.currentUser);
  const currentUser = auth.currentUser || auth.user;
  let idToken;
  if (currentUser) {
    idToken = await currentUser.getIdToken(true); // Force token refresh
    logger("refreshed ID token");
    localLogger("idToken:", idToken);
    const reloadedUser = await currentUser.reload();
    logger("reloaded user");
    localLogger("reloadedUser:", reloadedUser);
  } else {
    console.error("No current user found for token refresh.");
  }
  return idToken;
};

// Enhanced token manager
const tokenManager = {
  isRefreshing: false,
  refreshPromise: null,
  lastRefreshTime: 0, // Track when we last refreshed
  tokenRefreshCooldown: 5000, // 5 seconds cooldown between refresh attempts

  getValidToken: async () => {
    const now = Date.now();

    // If we recently refreshed, don't try again too soon
    if (
      now - tokenManager.lastRefreshTime <
      tokenManager.tokenRefreshCooldown
    ) {
      console.log("Token was just refreshed, using existing token");
      return auth.currentUser ? auth.currentUser.getIdToken() : null;
    }

    // If already refreshing, wait for that refresh to complete
    if (tokenManager.isRefreshing) {
      console.log("Token refresh already in progress, waiting...");
      return tokenManager.refreshPromise;
    }

    // Start a new refresh
    console.log("Starting new token refresh");
    tokenManager.isRefreshing = true;

    tokenManager.refreshPromise = getNewToken()
      .then((token) => {
        tokenManager.lastRefreshTime = Date.now();
        return token;
      })
      .finally(() => {
        tokenManager.isRefreshing = false;
      });

    return tokenManager.refreshPromise;
  },
};

// Track active API requests to prevent duplicates during token refresh
const pendingRequests = new Map();

/**
 * When JSON.parse fails on a JSON array string (e.g. truncated streamed body),
 * extract complete top-level `{...}` objects (brace depth + string awareness).
 * @param {string} text
 * @returns {object[] | null}
 */
function tryParseJsonArrayObjectsFromPrefix(text) {
  const t = text.trim().replace(/^\uFEFF/, "");
  if (!t.startsWith("[")) return null;
  const inner = t.slice(1);
  const objs = [];
  let depth = 0;
  let start = -1;
  let inString = false;
  let escape = false;
  for (let i = 0; i < inner.length; i++) {
    const c = inner[i];
    if (escape) {
      escape = false;
      continue;
    }
    if (c === "\\" && inString) {
      escape = true;
      continue;
    }
    if (c === '"') {
      inString = !inString;
      continue;
    }
    if (inString) continue;
    if (c === "{") {
      if (depth === 0) start = i;
      depth++;
    } else if (c === "}") {
      depth--;
      if (depth === 0 && start >= 0) {
        const slice = inner.slice(start, i + 1);
        try {
          objs.push(JSON.parse(slice));
        } catch {
          // skip malformed segment
        }
        start = -1;
      }
    }
  }
  return objs.length ? objs : null;
}

/**
 * Parse API payloads that may arrive as JSON or JSON-encoded JSON strings.
 * @param {string} responseBody
 * @returns {{ ok: true, data: any } | { ok: false, error: Error }}
 */
function parseApiResponseBody(responseBody) {
  let lastError = null;
  const candidates = [];
  const trimmed = responseBody.trim().replace(/^\uFEFF/, "");

  if (!trimmed) {
    return { ok: true, data: null };
  }

  candidates.push(trimmed);

  // Some endpoints return a quoted JSON blob, e.g. "\"[{...}]\"".
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    candidates.push(trimmed.slice(1, -1));
  }

  for (const candidate of candidates) {
    try {
      const parsed = JSON.parse(candidate);

      // Handle doubly-encoded JSON payloads.
      if (typeof parsed === "string") {
        const nested = parsed.trim();
        if (
          (nested.startsWith("[") && nested.endsWith("]")) ||
          (nested.startsWith("{") && nested.endsWith("}"))
        ) {
          try {
            return { ok: true, data: JSON.parse(nested) };
          } catch (nestedError) {
            lastError = nestedError;
          }
        }
      }

      return { ok: true, data: parsed };
    } catch (error) {
      lastError = error;
    }
  }

  const recovered = tryParseJsonArrayObjectsFromPrefix(trimmed);
  if (recovered) {
    return { ok: true, data: recovered };
  }

  return {
    ok: false,
    error: lastError || new Error("Unable to parse API response body as JSON."),
  };
}

/**
 * Build a useful API error message from status and parsed payload.
 * @param {number} status
 * @param {unknown} body
 * @returns {string}
 */
function extractApiErrorDetail(body) {
  if (typeof body === "string") {
    return body.trim();
  }
  if (!body || typeof body !== "object") {
    return "";
  }

  for (const key of ["message", "error", "detail", "reason", "title"]) {
    const candidate = body[key];
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
    if (candidate && typeof candidate === "object" && candidate !== body) {
      const nestedDetail = extractApiErrorDetail(candidate);
      if (nestedDetail) {
        return nestedDetail;
      }
    }
  }
  return "";
}

function buildApiErrorMessage(status, body) {
  if (status === 418) {
    return "Content was rejected by moderation. Please edit and resubmit. If this appears incorrect, report it for review.";
  }

  let detail = extractApiErrorDetail(body);
  if (typeof body === "string") {
    const raw = detail;
    const looksLikeHtml =
      /<\s*!doctype\s+html/i.test(raw) ||
      /<\s*html[\s>]/i.test(raw) ||
      /<\s*body[\s>]/i.test(raw) ||
      /<\s*meta[\s>]/i.test(raw) ||
      /<\s*title[\s>]/i.test(raw);
    if (looksLikeHtml) {
      if (status === 404) {
        detail = "Requested endpoint was not found on the server.";
      } else {
        detail = "Server returned an HTML error page.";
      }
    } else {
      detail = raw;
    }
  }
  const prefix = `Server response not OK (status ${status}).`;
  return detail ? `${prefix} ${detail}` : prefix;
}

/**
 * Health probes measure availability. They must not increment the API failure
 * circuit breaker or a down page that polls health will reload itself forever.
 *
 * @param {unknown} url
 * @returns {boolean}
 */
export function shouldTrackRequestFailure(url) {
  if (typeof url !== "string") {
    return true;
  }
  return !url.includes("/v1/public/global/misc/health/");
}

export async function apiRequestExternal(
  url,
  method = "GET",
  body = null,
  customHeaders = {},
  includeCredentials = false,
  shouldLog = true,
  makeJSON = true,
  timesToRetry = 0,
  retriedWithNewToken = false
) {
  // Create a request key for deduplication
  const requestKey = `${method}:${url}:${JSON.stringify(
    body
  )}:${retriedWithNewToken}`;

  // If this exact request is already in progress, wait for it instead of creating a duplicate
  if (pendingRequests.has(requestKey) && !retriedWithNewToken) {
    console.log("Similar request already in progress, waiting for result...");
    return pendingRequests.get(requestKey);
  }

  const requestPromise = apiRequestExternalImpl(
    url,
    method,
    body,
    customHeaders,
    includeCredentials,
    shouldLog,
    makeJSON,
    timesToRetry,
    retriedWithNewToken
  );

  // Store this request and remove it when done
  if (!retriedWithNewToken) {
    pendingRequests.set(requestKey, requestPromise);
    // Avoid creating an unhandled rejection from a discarded `.finally()` chain.
    requestPromise.then(
      () => {
        pendingRequests.delete(requestKey);
      },
      () => {
        pendingRequests.delete(requestKey);
      }
    );
  }

  return requestPromise;
}

export async function apiRequestExternalImpl(
  url,
  method = "GET",
  body = null,
  customHeaders = {},
  includeCredentials = false,
  shouldLog = true,
  makeJSON = true,
  timesToRetry = 0,
  retriedWithNewToken = false
) {
  const controller = new AbortController();
  const signal = controller.signal;
  let timeoutId;
  const isPublicAuthEndpoint = url.includes("/v1/public/global/get-auth/");
  const isAdminGatewayProbe = url.includes(
    "/v1/private/admin/user-admin/admin-gateway-check"
  );
  // Admin session hand-off redeem runs on the admin origin before any Firebase
  // session exists; a 401 (no/expired hand-off) must not trigger a token refresh
  // or sign-out — the caller falls back to a manual login instead.
  const isAdminHandoffRedeem = url.includes(
    "/v1/private/admin/session/redeem-handoff"
  );
  const trackFailures = shouldTrackRequestFailure(url);
  // /api/composer is same-origin and may sit behind IAP on admin hosts.
  // Force credentials include so browser sends IAP/session cookies.
  const isComposerProxyRequest =
    typeof url === "string" &&
    (url.startsWith("/api/composer") ||
      (typeof window !== "undefined" &&
        url.startsWith(`${window.location.origin}/api/composer`)));
  const shouldIncludeCredentials =
    includeCredentials || isComposerProxyRequest;

  try {
    // Request timeout tracking
    timeoutId = setTimeout(() => {
      controller.abort();
    }, config.timeoutSeconds * 1000);
    // let requestCompleted = false;

    // Conditionally log arguments
    if (process.env.NEXT_PUBLIC_NODE_ENV === "local") {
      localLogger(
        "args:",
        url,
        method,
        body,
        customHeaders,
        `includeCredentials: ${includeCredentials}`,
        `shouldLog: ${shouldLog}`,
        `timesToRetry: ${timesToRetry}`,
        `retriedWithNewToken: ${retriedWithNewToken}`
      );
    } else if (shouldLog) {
      logger(
        "args:",
        url,
        method,
        body,
        "customHeaders omitted",
        `includeCredentials: ${includeCredentials}`
      );
    }

    // Set default headers combined with any custom headers passed
    // Filter out empty Authorization headers to prevent CORS preflight failures
    const filteredCustomHeaders = { ...customHeaders };
    if (filteredCustomHeaders.Authorization === '' || 
        filteredCustomHeaders.Authorization === null || 
        filteredCustomHeaders.Authorization === undefined) {
      delete filteredCustomHeaders.Authorization;
    }
    
    const headers = {
      Accept: "application/json",
      ...(makeJSON && { "Content-Type": "application/json" }),
      ...filteredCustomHeaders,
    };

    // Make call, including any body if not a GET
    const response = await fetch(url, {
      method: method,
      headers: headers,
      credentials: shouldIncludeCredentials ? "include" : "omit",
      body:
        method !== "GET" ? (makeJSON ? JSON.stringify(body) : body) : undefined,
      signal: signal,
    });

    clearTimeout(timeoutId);

    // const fetchPromise = fetch(url, {
    //   method: method,
    //   headers: headers,
    //   credentials: includeCredentials ? "include" : "omit",
    //   ...(body && method !== "GET" && { body: JSON.stringify(body) }),
    // });

    // Timeout promise
    // setTimeout(() => {
    //   if (!requestCompleted) {
    //     incrementFailureCount();
    //     throw new Error(
    //       `Request timed out after ${config.timeoutSeconds} seconds.`
    //     );
    //   }
    // }, config.timeoutSeconds * 1000);

    // const response = await fetchPromise;
    // requestCompleted = true;

    // if (shouldLog) {
    //   logger("Raw composer response:", response);
    // } else {
    //   localLogger("Raw composer response:", response);
    // }
    // Parse response body
    const responseBody = await response.text();

    // if (shouldLog) {
    //   logger("responseBody:", responseBody);
    // } else {
    //   localLogger("responseBody:", responseBody);
    // }

    let data = null;

    if (responseBody) {
      const parseResult = parseApiResponseBody(responseBody);
      if (parseResult.ok) {
        data = parseResult.data;
        if (data && data.badges) {
          updateBadges(data.badges);
        }
      } else {
        if (shouldLog) {
          logger(
            "Response is not in JSON format. Handling as raw data. Err:",
            parseResult.error
          );
        }
        data = responseBody;
      }
    }

    // Throw error if composer response is not ok
    if (!response.ok) {
      // Handle 507
      if (response.status === 507) {
        console.error("Server overloaded.");
        updateSnackbar({
          message: "Server overloaded. Please try again soon.",
          severity: "error",
        });
      } else if (response.status >= 500 && response.status <= 599) {
        // Handle other 500s
        if (timesToRetry > 0) {
          console.error(`Got 500. Retrying in 3 seconds.`);
          await new Promise((resolve) => setTimeout(resolve, 3000));
          return apiRequestExternal(
            url,
            method,
            body,
            customHeaders,
            includeCredentials,
            shouldLog,
            makeJSON,
            timesToRetry - 1
          );
        } else if (trackFailures) {
          incrementFailureCount();
        }
      } else if (response.status === 401) {
        // Handle 401
        // Do not refresh tokens for public auth endpoints (login/oauth flows).
        // These endpoints use project/admin token semantics, not current Firebase user tokens.
        if (isPublicAuthEndpoint || isAdminGatewayProbe || isAdminHandoffRedeem) {
          logger(
            "401 on auth/probe endpoint; skipping token refresh retry and sign-out."
          );
          const error = new Error("Server response not OK.");
          error.status = response.status;
          if (responseBody) {
            const parsedError = parseApiResponseBody(responseBody);
            error.body = parsedError.ok ? parsedError.data : responseBody;
          } else {
            error.body = null;
          }
          error.message = buildApiErrorMessage(response.status, error.body);
          throw error;
        }

        // Refresh Firebase token & retry just 1x if unauthorized error
        if (!retriedWithNewToken) {
          // const token = await getNewToken();
          const token = await tokenManager.getValidToken();
          if (token) {
            return apiRequestExternal(
              url,
              method,
              body,
              { ...customHeaders, Authorization: token },
              includeCredentials,
              shouldLog,
              makeJSON,
              timesToRetry,
              true
            );
          }
        } else {
          logger("Retried with new token, still 401. Logging out of Firebase.");
          auth?.signOut();
        }
        // Handle other errors
      } else if (response.status) {
        resetFailureCount();
      }
      // Throw error for all non-OK status codes
      const error = new Error("Server response not OK.");
      error.status = response.status;
      if (responseBody) {
        const parsedError = parseApiResponseBody(responseBody);
        error.body = parsedError.ok ? parsedError.data : responseBody;
      } else {
        error.body = null;
      }
      error.message = buildApiErrorMessage(response.status, error.body);
      throw error;
    }

    // Reset failure count if successful
    resetFailureCount();

    // Return a useful response object
    return {
      data: data,
      status: response.status,
    };
  } catch (err) {
    clearTimeout(timeoutId);
    // Handle major server/network errors
    if (err instanceof TypeError) {
      if (shouldLog) {
        const requestContext = `[${method}] ${url}`;
        if (err.message.includes("NetworkError")) {
          console.error(
            "Network error. This might be due to offline status or DNS failure.",
            requestContext
          );
        } else {
          console.error(
            "Possible CORS or unexpected network issue:",
            err.message,
            requestContext
          );
        }
      }
      if (trackFailures) {
        incrementFailureCount();
      }
    }

    if (err.name === "AbortError") {
      err.status = 408;
      console.error(`Request timed out after ${config.timeoutSeconds} seconds`);
      if (trackFailures) {
        incrementFailureCount();
      }
    }

    // Callers pass shouldLog=false for optional probes (hub filters, blog status, etc.).
    if (shouldLog) {
      logger(err);
      if (err.status) {
        console.error(`Error with status ${err.status} occurred.`);
      }
      if (err.message) {
        logger("Error message:", err.message);
      }
      if (err.body) {
        logger("Body:", err.body);
      }
      if (err.response) {
        logger("Response:", err.response);
      }
      logger("Full error object:", JSON.stringify(err, null, 2));
    }
    throw err;
  }
}
